import { z } from 'zod';
import {
  exportAppearanceOptionsSchema,
  exportDressingOptionsSchema,
  exportPageOptionsSchema,
  exportTableOptionsSchema,
} from './export.js';

/**
 * Personal export presets — export-rework chantier 5 (issue #13, spec P1/P3/T2).
 *
 * A user saves the four PDF option blocks under a name and re-applies them on
 * any matrix. The stored shape is the chantier-2 four-block snapshot, so a
 * personal preset and a built-in one are interchangeable once applied.
 */

export const EXPORT_PRESET_NAME_MAX = 60;
export const MAX_EXPORT_PRESETS_PER_USER = 30;

/**
 * Trimmed before the length checks (Zod v3 runs `.trim()` first because it is
 * declared first), case-sensitive, and free of control characters — a name is
 * shown on one line in the picker.
 */
export const exportPresetNameSchema = z
  .string()
  .trim()
  .min(1)
  .max(EXPORT_PRESET_NAME_MAX)
  .regex(/^[^\p{Cc}]+$/u);

/**
 * STRICT four-block snapshot: the write path and the response shape. The read
 * path never parses a stored row with this directly — it goes through
 * `repairPresetSettings` so a row saved under an older schema still loads.
 */
export const exportPresetSettingsSchema = z.object({
  page: exportPageOptionsSchema,
  table: exportTableOptionsSchema,
  dressing: exportDressingOptionsSchema,
  appearance: exportAppearanceOptionsSchema,
});
export type ExportPresetSettings = z.infer<typeof exportPresetSettingsSchema>;

/** `POST /me/export-presets` body — an upsert keyed by the trimmed name. */
export const saveExportPresetInputSchema = z.object({
  name: exportPresetNameSchema,
  settings: exportPresetSettingsSchema,
});
export type SaveExportPresetInput = z.infer<typeof saveExportPresetInputSchema>;

export const userExportPresetSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  settings: exportPresetSettingsSchema,
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type UserExportPreset = z.infer<typeof userExportPresetSchema>;

/** `GET /me/export-presets` */
export const userExportPresetListSchema = z.array(userExportPresetSchema);
